import { Group, Schedule } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import React, { useState } from 'react';

import ApprovalHistoryCard from './ApprovalHistoryCard';
import ApprovalRejectionDialog from './ApprovalRejectionDialog';
import ApprovalRoleCard from './ApprovalRoleCard';
import { CommitteeTabsProps } from './types';

const CommitteeTabs = ({
  table,
  setTable,
  approvalsRequired,
  approvals,
  handleApprove,
  handleReject,
  canApprove,
  canEdit,
  onDeleteApproval,
  canDeleteApproval,
  userRoles,
  selectedUserRoleId,
  setSelectedUserRoleId,
  isActive,
  loading = false,
}: CommitteeTabsProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'approve' | 'reject'>('approve');
  const totalRequiredApprovals = approvalsRequired.reduce(
    (acc, curr) =>
      acc + (curr.approvedCountRequired ? curr.approvedCountRequired : 0),
    0,
  );
  const totalApprovals = approvalsRequired.reduce(
    (acc, curr) => acc + (curr.approvedCount ? curr.approvedCount : 0),
    0,
  );
  const totalRejected = approvalsRequired.reduce(
    (acc, curr) => acc + (curr.rolesAlreadyRejected ? 1 : 0),
    0,
  );
  const missingApprovals =
    totalRequiredApprovals - totalApprovals - totalRejected;

  const handleApproveClick = () => {
    setDialogType('approve');
    setDialogOpen(true);
  };

  const handleRejectClick = () => {
    setDialogType('reject');
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (reason: string) => {
    if (!selectedUserRoleId) return;
    if (dialogType === 'approve') {
      await handleApprove(reason, selectedUserRoleId);
    } else {
      await handleReject(reason, selectedUserRoleId);
    }
    setSelectedUserRoleId(null);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setSelectedUserRoleId(null);
  };

  if (loading) return;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          p: 0.5,
          mb: 3,
        }}
      >
        {!isActive && (
          <Box
            onClick={() => setTable('comitee')}
            sx={{
              flex: 1,
              py: 1.5,
              px: 2,
              borderRadius: 1.5,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: table === 'comitee' ? 'white' : 'transparent',
              boxShadow:
                table === 'comitee' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor:
                  table === 'comitee' ? 'white' : 'rgba(255,255,255,0.5)',
              },
            }}
          >
            <Group
              sx={{
                fontSize: '1.1rem',
                color: table === 'comitee' ? 'text.main' : 'text.secondary',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: table === 'comitee' ? 600 : 500,
                color: table === 'comitee' ? 'text.primary' : 'text.secondary',
                fontSize: '0.9rem',
              }}
            >
              Aprobaciones Pendientes
            </Typography>
            {missingApprovals > 0 && (
              <Box
                sx={{
                  backgroundColor: 'warning.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  ml: 0.5,
                }}
              >
                {missingApprovals}
              </Box>
            )}
          </Box>
        )}
        <Box
          onClick={() => setTable('approvals')}
          sx={{
            flex: 1,
            py: 1.5,
            px: 2,
            borderRadius: 1.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            backgroundColor: table === 'approvals' ? 'white' : 'transparent',
            boxShadow:
              table === 'approvals' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor:
                table === 'approvals' ? 'white' : 'rgba(255,255,255,0.5)',
            },
          }}
        >
          <Schedule
            sx={{
              fontSize: '1.1rem',
              color: table === 'approvals' ? 'text.main' : 'text.secondary',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: table === 'approvals' ? 600 : 500,
              color: table === 'approvals' ? 'text.primary' : 'text.secondary',
              fontSize: '0.9rem',
            }}
          >
            Historial
          </Typography>
          {approvals.length > 0 && (
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
                ml: 0.5,
              }}
            >
              {approvals.length}
            </Box>
          )}
        </Box>
      </Box>

      {table === 'comitee' ? (
        <Box>
          {approvalsRequired.length === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Todos los roles han sido aprobados correctamente.
              </Typography>
            </Alert>
          ) : (
            approvalsRequired.map((role, index) => (
              <ApprovalRoleCard
                key={index}
                role={role}
                index={index}
                onApprove={handleApproveClick}
                onReject={handleRejectClick}
                canApprove={canApprove && canEdit}
              />
            ))
          )}
        </Box>
      ) : (
        <Box>
          {approvals.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No hay aprobaciones registradas aún.
              </Typography>
            </Alert>
          ) : (
            approvals.map((approval, index) => (
              <ApprovalHistoryCard
                key={index}
                approval={approval}
                index={index}
                onDeleteApproval={onDeleteApproval}
                canDeleteApproval={canDeleteApproval}
              />
            ))
          )}
        </Box>
      )}

      <ApprovalRejectionDialog
        open={dialogOpen}
        type={dialogType}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
        setOpen={setDialogOpen}
        userRoles={userRoles}
        selectedUserRoleId={selectedUserRoleId}
        setSelectedUserRoleId={setSelectedUserRoleId}
      />
    </Box>
  );
};

export default CommitteeTabs;
